import { newTemplateFilterEnum } from "../../../src/taskpane/components/enums/newTemplateFilterEnum";
import { IPageSettings } from "../../../src/taskpane/interfaces/IPageSettings";

export const rootContentType = "root";
export const sharePointFolderContentTypeIds = {
  folder: "0x0120",
  documentSet: "0x0120D5",
} as const;

export type TemplateContentType =
  | typeof rootContentType
  | typeof sharePointFolderContentTypeIds.folder
  | typeof sharePointFolderContentTypeIds.documentSet;

const isCurrentDirectoryInFolder = (currentDirectory?: IPageSettings): boolean =>
  Boolean(
    (currentDirectory?.contentTypeId?.startsWith(sharePointFolderContentTypeIds.folder) && currentDirectory?.serverRelativePath)
    || (currentDirectory?.directory?.length > 0 && currentDirectory?.parentId)
  );

export const getCurrentTemplateContentType = ({
  currentDirectory,
}: {
  currentDirectory?: IPageSettings;
  enableNewTemplateFilters?: boolean;
  enforceStrictDocumentSetsOnly?: boolean;
}): TemplateContentType => {
  if (currentDirectory?.contentTypeId?.startsWith(sharePointFolderContentTypeIds.documentSet)) {
    return sharePointFolderContentTypeIds.documentSet;
  }

  if (isCurrentDirectoryInFolder(currentDirectory)) {
    return sharePointFolderContentTypeIds.folder;
  }

  return rootContentType;
};

export const isTemplateFilterAllowed = ({
  currentContentType,
  filter,
  newTemplateDocumentSetFilters,
  newTemplateFolderFilters,
  newTemplateLibraryFilters,
}: {
  currentContentType: TemplateContentType;
  filter: newTemplateFilterEnum;
  newTemplateDocumentSetFilters?: string | newTemplateFilterEnum[];
  newTemplateFolderFilters?: string | newTemplateFilterEnum[];
  newTemplateLibraryFilters?: string | newTemplateFilterEnum[];
}): boolean => {
  const hasFilter = (filters?: string | newTemplateFilterEnum[]) => {
    if (filters === undefined || filters === null) {
      return true;
    }

    if (Array.isArray(filters)) {
      return filters.includes(filter);
    }

    if (typeof filters === "string") {
      try {
        const parsedFilters = JSON.parse(filters);
        if (Array.isArray(parsedFilters)) {
          return parsedFilters.map(value => value?.toString()?.trim()).includes(filter);
        }
      } catch (error) {
        // Ignore JSON parsing errors and fall back to delimiter parsing.
      }

      return filters
        .split(/[;,]/)
        .map(value => value.trim())
        .includes(filter);
    }

    return false;
  };

  if (currentContentType === rootContentType) {
    return hasFilter(newTemplateLibraryFilters);
  }

  if (currentContentType === sharePointFolderContentTypeIds.folder) {
    return hasFilter(newTemplateFolderFilters);
  }

  return hasFilter(newTemplateDocumentSetFilters);
};
